// src/components/admin/RoleList.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  IconButton,
  TablePagination,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  Breadcrumbs,
  Link,
  Alert,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import roleApi from "../../api/roles";
import { getTenantsList } from "../../api/tenant";
import useAuth from "../../hooks/useAuth";
import { formatAxiosError } from "../../utils/apiError";
import { APP_HOME_PATH } from "../../config/routes";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard, {
  MobileListCardSkeleton,
} from "../../components/MobileListCard";

function RoleCard({ role, onEdit, onDelete }) {
  return (
    <MobileListCard
      title={role.name}
      subtitle={role.description || "—"}
      fields={[
        {
          label: "Permissions",
          value: (
            <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
              {role.permissions?.map((permission) => (
                <Chip key={permission} label={permission} size="small" />
              ))}
            </Stack>
          ),
        },
      ]}
      actions={
        <>
          <IconButton aria-label="Edit role" onClick={() => onEdit(role.id)}>
            <Edit />
          </IconButton>
          <IconButton aria-label="Delete role" onClick={() => onDelete(role.id)}>
            <Delete />
          </IconButton>
        </>
      }
    />
  );
}

const RoleList = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobileList("md");
  const { user: currentUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(null);
  const [listError, setListError] = useState(null);

  const isSuperAdmin = currentUser?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await getTenantsList({ for_filter: true });
        if (!cancelled) setTenants(Array.isArray(raw) ? raw : []);
      } catch {
        if (!cancelled) setTenants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    setPage(0);
  }, [tenantFilter]);

  useEffect(() => {
    let cancelled = false;
    const fetchRoles = async () => {
      setListError(null);
      if (isSuperAdmin && tenantFilter?.id == null) {
        setRoles([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const tenantId = isSuperAdmin ? tenantFilter.id : undefined;
        const response = await roleApi.getRolesList({
          skip: 0,
          limit: 200,
          tenant_id: tenantId,
        });
        const arr = Array.isArray(response) ? response : response?.items ?? [];
        if (!cancelled) setRoles(arr);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch roles:", error);
          setListError(formatAxiosError(error, "Could not load roles"));
          setRoles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRoles();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, tenantFilter, currentUser?.tenant_id]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (roleId) => {
    try {
      await roleApi.deleteRole(roleId);
      setRoles(roles.filter((role) => role.id !== roleId));
    } catch (error) {
      console.error("Failed to delete role:", error);
    }
  };

  const pagedRows = roles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const goCreate = () => {
    if (isSuperAdmin) {
      if (tenantFilter?.id == null) return;
      navigate(`/admin/roles/create?tenant_id=${tenantFilter.id}`);
      return;
    }
    navigate("/admin/roles/create");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Typography color="text.primary">Roles</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        Role management
      </Typography>

      {isSuperAdmin && (
        <Box sx={{ maxWidth: 480, mb: 2 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={(_, v) => setTenantFilter(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization"
                placeholder="Select to load roles"
                helperText="Platform admins must choose an organization (roles are per farm)."
              />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      )}

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      {isSuperAdmin && tenantFilter == null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization to view and manage its roles.
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={goCreate}
        disabled={isSuperAdmin && tenantFilter == null}
        sx={{ mb: 2 }}
      >
        Create new role
      </Button>

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <MobileListCardSkeleton key={i} />
              ))
            : pagedRows.length === 0
              ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                  {isSuperAdmin && tenantFilter == null
                    ? "Choose an organization above."
                    : "No roles in this list."}
                </Typography>
              )
              : pagedRows.map((role) => (
                  <RoleCard
                    key={role.id}
                    role={role}
                    onEdit={(id) => navigate(`/admin/roles/${id}/edit`)}
                    onDelete={handleDelete}
                  />
                ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary">
                      {isSuperAdmin && tenantFilter == null
                        ? "Choose an organization above."
                        : "No roles in this list."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell>
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        {role.permissions?.map((permission) => (
                          <Chip key={permission} label={permission} size="small" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        aria-label="Edit role"
                        onClick={() => navigate(`/admin/roles/${role.id}/edit`)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        aria-label="Delete role"
                        onClick={() => handleDelete(role.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={roles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default RoleList;
